import { gql } from "@apollo/client";
import { PageData } from "../hooks/useWebpageData";

const accountPath = "account";
const webpagePath = "webpage";
const imagePath = "image";
const publishPath = webpagePath + "/publish";
const autoFillPath = webpagePath + "/autoFill";
const checkoutSessionPath = "checkoutSession";

export interface AccountData {
  account: {
    accountId: string;
    email: string;
    fullName: string;
    createdOn: string;
    updatedOn: string;
    webpageVersion: number;
    webpageUrl: string;
    webpageSlug: string;
    webpageSlugUpdatedOn: string;
    webpageLastPublished: string;
    webpageLastPublishedBy: string;
    webpageGoogleTag: string;
    webpageGoogleTagUpdatedOn: string;
    domainName: string;
    distributionId: string;
    distributionDomain: string;
    subscriptionStatus: string;
    subscriptionTier: string;
    subscriptionInterval: string;
    subscriptionStartedOn: string;
    subscriptionUpdatedOn: string;
  };
}

export const GET_ACCOUNT_QUERY = gql`
  query Account($metaFullName: any!) {
    account(metaFullName: $metaFullName) 
    @rest(type: "Account", path: "${accountPath}?{args}") {
      accountId,
      email,
      fullName,
      createdOn,
      updatedOn,
      webpageVersion,
      webpageUrl,
      webpageSlug,
      webpageSlugUpdatedOn,
      webpageLastPublished,
      webpageLastPublishedBy,
      webpageGoogleTag,
      webpageGoogleTagUpdatedOn,
      domainName,
      distributionId
      distributionDomain,
      subscriptionStatus,
      subscriptionTier,
      subscriptionInterval,
      subscriptionStartedOn,
      subscriptionUpdatedOn,
    }
  }
`;

export const PATCH_ACCOUNT_MUTATION = gql`
  mutation AccountUpsert($input: any!) {
    account: upsert(input: $input)
      @rest(
        type: "Account"
        path: "${accountPath}/"
        method: "PATCH"
      ) {
      accountId,
      email,
      fullName,
      createdOn,
      updatedOn,
      webpageVersion,
      webpageUrl,
      webpageSlug,
      webpageSlugUpdatedOn,
      webpageLastPublished,
      webpageLastPublishedBy,
      webpageGoogleTag,
      webpageGoogleTagUpdatedOn,
    }
  }
`;

export interface WebpageData {
  webpage: PageData;
}

export const GET_WEBPAGE_QUERY = gql`
  query Webpage($version: any!) {
    webpage(version: $version) 
    @rest(type: "Webpage", path: "${webpagePath}?{args}") {
      accountId,
      webpageVersion,
      companyName,
      companyTitle,
      companyDescription,
      aboutMeTitle,
      aboutMe,
      logo,
      serviceItemsTitle,
      serviceItems,
      otherTitle,
      otherItems,
      email,
      phone,
      socialUrl,
      owner,
      addressFreeForm,
      otherImg,
      backgroundImg,
      enableSectionOne,
      enableSectionTwo,
      enableSectionThree,
      ctaText,
      ctaLink,
      ctaEnabled,
      primaryColor,
      secondaryColor,
    }
  }
`;

export const UPSERT_WEBPAGE_MUTATION = gql`
  mutation WebpageUpsert($input: any!) {
    webpage: upsert(input: $input)
      @rest(
        type: "Webpage"
        path: "${webpagePath}/"
        method: "PUT"
      ) {
      accountId,
      webpageVersion,
      companyName,
      companyTitle,
      companyDescription,
      aboutMeTitle,
      aboutMe,
      logo,
      serviceItemsTitle,
      serviceItems,
      otherTitle,
      otherItems,
      email,
      phone,
      socialUrl,
      owner,
      addressFreeForm,
      otherImg,
      backgroundImg,
      enableSectionOne,
      enableSectionTwo,
      enableSectionThree,
      ctaText,
      ctaLink,
      ctaEnabled,
      primaryColor,
      secondaryColor,
    }
  }
`;

export interface PublishData {
  publish: {
    publishVersion: string;
    publishUrl: string;
  };
}

export const POST_PUBLISH_WEBPAGE_MUTATION = gql`
  mutation PublishPost($input: any!) {
    publish: post(input: $input)
      @rest(
        type: "Publish"
        path: "${publishPath}/"
        method: "POST"
      ) {
      publishVersion,
      publishUrl,
    }
  }
`;

export interface PreSignedUrlData {
  image: {
    presignedUrl: string;
    fullKey: string;
    errorMessage?: string;
  };
}

export const POST_PRE_SIGNED_URL_MUTATION = gql`
  mutation ImagePost($input: any!) {
    image: post(input: $input)
      @rest(
        type: "Image"
        path: "${imagePath}/"
        method: "POST"
      ) {
      presignedUrl,
      fullKey,
    }
  }
`;

export interface CheckoutSessionData {
  checkoutSession: {
    client_secret: string;
    status: string;
    payment_status: string;
  };
}

export const POST_CHECKOUT_SESSION = gql`
  mutation CheckoutSession($input: any!) {
    checkoutSession: post(input: $input)
      @rest(
        type: "CheckoutSession"
        path: "${checkoutSessionPath}/"
        method: "POST"
      ) {
      client_secret,
      status,
      payment_status,
    }
  }
`;

export interface AutoFillData {
  autoFill: {
    rawResponse: any;
    aiChoices: {
      index: number;
      message: {
        role: string;
        content: string;
      };
      finish_reason: string;
    }[];
    industry: string;
  };
}

export const GET_AUTOFILL_QUERY = gql`
  query AutoFill($industry: any!) {
    autoFill(industry: $industry) 
    @rest(type: "AutoFill", path: "${autoFillPath}?{args}") {
      aiChoices,
      industry,
    }
  }
`;
